import { useState, useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { artists, artistRewards, myArtistXP } from "@/mocks/artistData";
import type { Reward } from "@/mocks/artistData";
import Navbar from "@/pages/home/components/Navbar";
import RewardCard from "@/pages/rewards/components/RewardCard";
import RewardDetailModal from "@/pages/rewards/components/RewardDetailModal";

type SearchMode = "reward" | "artist";

const sortOptions = [
  { value: "xp-asc", label: "XP 낮은 순" },
  { value: "xp-desc", label: "XP 높은 순" },
  { value: "stock-asc", label: "잔여 수량 적은 순" },
];

export default function RewardsPage() {
  const [searchMode, setSearchMode] = useState<SearchMode>("reward");
  const [rewardQuery, setRewardQuery] = useState("");
  const [selectedArtistId, setSelectedArtistId] = useState<string>("all");
  const [artistInputValue, setArtistInputValue] = useState("");
  const [artistDropdownOpen, setArtistDropdownOpen] = useState(false);
  const [modeMenuOpen, setModeMenuOpen] = useState(false);
  const [sortBy, setSortBy] = useState("xp-asc");
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const modeMenuRef = useRef<HTMLDivElement>(null);
  const artistDropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (modeMenuRef.current && !modeMenuRef.current.contains(e.target as Node)) setModeMenuOpen(false);
      if (
        artistDropRef.current && !artistDropRef.current.contains(e.target as Node) &&
        wrapperRef.current && !wrapperRef.current.contains(e.target as Node)
      ) setArtistDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const getMyXP = (artistId: string) => myArtistXP.find((x) => x.artistId === artistId)?.xp ?? 0;
  const selectedArtistData = artists.find((a) => a.id === selectedArtistId);
  const artistDropList = artists.filter((a) =>
    artistInputValue.trim() === "" ? true : a.name.toLowerCase().includes(artistInputValue.trim().toLowerCase())
  );

  const handleModeChange = (mode: SearchMode) => {
    setSearchMode(mode);
    setModeMenuOpen(false);
    setRewardQuery("");
    setSelectedArtistId("all");
    setArtistInputValue("");
    setArtistDropdownOpen(false);
  };

  const handleArtistSelect = (id: string) => {
    setSelectedArtistId(id);
    const found = artists.find((a) => a.id === id);
    setArtistInputValue(found ? found.name : "");
    setArtistDropdownOpen(false);
  };

  const filteredRewards = useMemo(() => {
    let result = [...artistRewards];
    if (selectedArtistId !== "all") result = result.filter((r) => r.artistId === selectedArtistId);
    if (rewardQuery.trim()) {
      const q = rewardQuery.trim().toLowerCase();
      result = result.filter((r) => r.title.toLowerCase().includes(q) || r.description.toLowerCase().includes(q));
    }
    if (sortBy === "xp-asc") result.sort((a, b) => a.xpCost - b.xpCost);
    else if (sortBy === "xp-desc") result.sort((a, b) => b.xpCost - a.xpCost);
    else if (sortBy === "stock-asc") result.sort((a, b) => a.stock - b.stock);
    return result;
  }, [rewardQuery, selectedArtistId, sortBy]);

  const totalRedeemable = artistRewards.filter((r) => r.xpCost <= getMyXP(r.artistId)).length;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />

      {/* Hero — same style as Campaigns / Creators */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/f23cb373ac0aabea3818e78983932778.jpg"
            alt="rewards hero"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f]/60 via-[#0a0a0f]/40 to-[#0a0a0f]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-24 pb-16">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-white/40 mb-6">
            <Link to="/" className="hover:text-white/70 transition-colors cursor-pointer">홈</Link>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs" />
            </div>
            <span className="text-white/70">리워드</span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#ffd60a]/20 to-[#ff8c42]/20 border border-white/10">
                  <i className="ri-gift-line text-xl text-[#ffd60a]" />
                </div>
                <span className="text-[#ffd60a] text-sm font-semibold tracking-widest uppercase">XP Rewards</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-black text-white leading-tight">
                퀘스트 XP로 교환하는
                <br />
                <span className="bg-gradient-to-r from-[#ffd60a] via-[#ff8c42] to-[#f72585] bg-clip-text text-transparent">
                  독점 리워드
                </span>
              </h1>
              <p className="text-white/50 mt-3 text-base max-w-xl">
                팬미팅 초청권, 뮤직비디오 크레딧, 시사회 초청 등 {artistRewards.length}개의 독점 혜택을 만나보세요.
              </p>
            </div>

            {/* XP Summary */}
            <div className="flex items-center gap-5 px-6 py-4 rounded-2xl bg-white/5 border border-white/10 flex-shrink-0">
              <div>
                <div className="text-white/40 text-xs mb-1">교환 가능</div>
                <div className="flex items-center gap-1.5">
                  <i className="ri-gift-line text-[#39ff14]" />
                  <span className="text-[#39ff14] font-black text-xl">{totalRedeemable}</span>
                  <span className="text-white/40 text-sm">개</span>
                </div>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div>
                <div className="text-white/40 text-xs mb-1">전체 리워드</div>
                <div className="flex items-center gap-1.5">
                  <i className="ri-coin-line text-[#ffd60a]" />
                  <span className="text-white font-black text-xl">{artistRewards.length}</span>
                  <span className="text-white/40 text-sm">개</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-24">

        {/* Search Bar */}
        <div className="flex items-center gap-3 mb-8 pt-6">
          <div className="flex items-center flex-1 relative" ref={wrapperRef}>
            <div className="flex items-center flex-1 bg-[#12121a] border border-white/10 rounded-xl focus-within:border-white/25 transition-colors">
              {/* Mode Toggle */}
              <div className="relative flex-shrink-0" ref={modeMenuRef}>
                <button
                  onClick={() => setModeMenuOpen((p) => !p)}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold hover:text-white transition-colors cursor-pointer whitespace-nowrap border-r border-white/10 rounded-l-xl bg-white/5"
                >
                  <i className={searchMode === "reward" ? "ri-gift-line text-[#ffd60a]" : "ri-user-star-line text-[#ff6eb4]"} />
                  <span className={searchMode === "reward" ? "text-[#ffd60a]" : "text-[#ff6eb4]"}>
                    {searchMode === "reward" ? "리워드" : "아티스트"}
                  </span>
                  <i className={`ri-arrow-down-s-line text-xs text-white/40 transition-transform duration-200 ${modeMenuOpen ? "rotate-180" : ""}`} />
                </button>
                {modeMenuOpen && (
                  <div className="absolute top-[calc(100%+6px)] left-0 w-36 bg-[#1a1a24] border border-white/15 rounded-xl overflow-hidden z-[100]">
                    <button
                      onClick={() => handleModeChange("reward")}
                      className={`w-full flex items-center gap-2.5 px-4 py-3 text-sm cursor-pointer transition-colors ${searchMode === "reward" ? "text-[#ffd60a] bg-[#ffd60a]/10" : "text-white/60 hover:text-white hover:bg-white/5"}`}
                    >
                      <i className="ri-gift-line" />리워드
                    </button>
                    <button
                      onClick={() => handleModeChange("artist")}
                      className={`w-full flex items-center gap-2.5 px-4 py-3 text-sm cursor-pointer transition-colors ${searchMode === "artist" ? "text-[#ff6eb4] bg-[#ff6eb4]/10" : "text-white/60 hover:text-white hover:bg-white/5"}`}
                    >
                      <i className="ri-user-star-line" />아티스트
                    </button>
                  </div>
                )}
              </div>

              {/* Input */}
              <div className="relative flex-1">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                  {searchMode === "artist" && selectedArtistId !== "all" && selectedArtistData ? (
                    <div className="w-4 h-4 rounded-full overflow-hidden">
                      <img src={selectedArtistData.avatar} alt={selectedArtistData.name} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <i className="ri-search-line text-white/30 text-sm" />
                  )}
                </div>
                {searchMode === "reward" ? (
                  <input
                    type="text"
                    value={rewardQuery}
                    onChange={(e) => setRewardQuery(e.target.value)}
                    placeholder="리워드 제목, 설명으로 검색..."
                    className="w-full bg-transparent pl-10 pr-10 py-2.5 text-sm text-white placeholder-white/25 focus:outline-none"
                  />
                ) : (
                  <input
                    type="text"
                    value={artistInputValue}
                    onChange={(e) => { setArtistInputValue(e.target.value); setSelectedArtistId("all"); setArtistDropdownOpen(true); }}
                    onFocus={() => setArtistDropdownOpen(true)}
                    placeholder="아티스트 이름으로 검색..."
                    className="w-full bg-transparent pl-10 pr-10 py-2.5 text-sm text-white placeholder-white/25 focus:outline-none"
                  />
                )}
                {(rewardQuery || artistInputValue || selectedArtistId !== "all") && (
                  <button
                    onClick={() => { setRewardQuery(""); setArtistInputValue(""); setSelectedArtistId("all"); setArtistDropdownOpen(false); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-white/30 hover:text-white/60 cursor-pointer transition-colors"
                  >
                    <i className="ri-close-line text-base" />
                  </button>
                )}
              </div>
            </div>

            {/* Artist Dropdown */}
            {searchMode === "artist" && artistDropdownOpen && (
              <div ref={artistDropRef} className="absolute top-[calc(100%+6px)] left-0 right-0 bg-[#1a1a24] border border-white/15 rounded-xl overflow-hidden z-[100] max-h-60 overflow-y-auto">
                <button
                  onClick={() => { handleArtistSelect("all"); setArtistInputValue(""); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm cursor-pointer transition-colors ${selectedArtistId === "all" ? "text-white bg-white/10" : "text-white/60 hover:text-white hover:bg-white/5"}`}
                >
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10 flex-shrink-0">
                    <i className="ri-apps-line text-xs text-white/60" />
                  </div>
                  <span>전체 아티스트</span>
                  {selectedArtistId === "all" && <i className="ri-check-line ml-auto text-[#ffd60a]" />}
                </button>
                {artistDropList.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => handleArtistSelect(a.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm cursor-pointer transition-colors ${selectedArtistId === a.id ? "text-white bg-white/10" : "text-white/60 hover:text-white hover:bg-white/5"}`}
                  >
                    <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0">
                      <img src={a.avatar} alt={a.name} className="w-full h-full object-cover" />
                    </div>
                    <span>{a.name}</span>
                    <span className="ml-auto text-xs text-white/30">{a.categoryLabel}</span>
                    {selectedArtistId === a.id && <i className="ri-check-line text-[#ffd60a]" />}
                  </button>
                ))}
                {artistDropList.length === 0 && (
                  <div className="px-4 py-4 text-sm text-white/30 text-center">검색 결과 없음</div>
                )}
              </div>
            )}
          </div>

          {/* Sort */}
          <div className="relative flex-shrink-0">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="appearance-none bg-[#12121a] border border-white/10 rounded-xl px-4 py-2.5 pr-8 text-sm text-white/70 focus:outline-none focus:border-white/25 cursor-pointer transition-colors whitespace-nowrap"
            >
              {sortOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
              <i className="ri-arrow-down-s-line text-white/40 text-sm" />
            </div>
          </div>

          <span className="text-white/35 text-sm whitespace-nowrap flex-shrink-0">
            <span className="text-white font-semibold">{filteredRewards.length}</span>개
          </span>
        </div>

        {/* Results hint */}
        <div className="flex justify-end mb-6">
          <div className="flex items-center gap-2 text-white/30 text-xs">
            <i className="ri-information-line" />
            교환 가능한 리워드는 <span className="text-[#39ff14]">초록색 테두리</span>로 표시됩니다
          </div>
        </div>

        {/* Reward Grid */}
        {filteredRewards.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRewards.map((reward) => {
              const artist = artists.find((a) => a.id === reward.artistId);
              return (
                <RewardCard
                  key={reward.id}
                  reward={reward}
                  artist={artist}
                  myXP={getMyXP(reward.artistId)}
                  onSelect={() => setSelectedReward(reward)}
                />
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20 text-white/30">
            <i className="ri-gift-line text-4xl mb-4 block" />
            <p>해당 조건의 리워드가 없습니다.</p>
          </div>
        )}

        {/* Creator CTA */}
        <div className="mt-20 rounded-2xl border border-white/10 bg-white/5 p-10 text-center">
          <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#ffd60a]/10 border border-[#ffd60a]/30 mx-auto mb-5">
            <i className="ri-settings-line text-2xl text-[#ffd60a]" />
          </div>
          <h3 className="text-white font-black text-xl mb-2">크리에이터이신가요?</h3>
          <p className="text-white/40 text-sm mb-6 max-w-sm mx-auto">
            팬들에게 제공할 리워드를 직접 세팅하세요. 팬미팅, 시사회 초청권, 크레딧 등재 등 다양한 리워드를 설정할 수 있습니다.
          </p>
          <Link
            to="/rewards/manage"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#ffd60a] text-[#0a0a0f] font-bold text-sm hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-circle-line" />
            리워드 세팅하기
          </Link>
        </div>
      </div>

      {selectedReward && (
        <RewardDetailModal
          reward={selectedReward}
          artist={artists.find((a) => a.id === selectedReward.artistId)}
          myXP={getMyXP(selectedReward.artistId)}
          onClose={() => setSelectedReward(null)}
        />
      )}
    </div>
  );
}
